// =============================================
// WebsiteSchemaDialog — master toggle Schema.org per website.
// Wołany z WebsitesTable przez ikonkę FileCode2 per row.
//
// 2026-05-17: BEZ Organization data. PBN-friendly schema = WebPage → BlogPosting → Person.
// Kolumny `websites.organization_*` zostają w DB jako legacy (backward compat dla rollback),
// ale runtime ignorowane przez nowy schemaGenerator.
// =============================================

import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, FileCode2, Info, ShieldCheck } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useWebsites, type Website } from "@/hooks/useWebsites";

interface WebsiteSchemaDialogProps {
  website: Website | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function WebsiteSchemaDialog({ website, open, onOpenChange }: WebsiteSchemaDialogProps) {
  const { toast } = useToast();
  const { updateWebsite } = useWebsites();
  const [schemaEnabled, setSchemaEnabled] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (website && open) {
      setSchemaEnabled(website.schema_enabled ?? false);
    } else if (!open) {
      setSchemaEnabled(false);
    }
  }, [website, open]);

  if (!website) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const ok = await updateWebsite(website.id, { schema_enabled: schemaEnabled });
      if (ok) {
        toast({
          title: "Zapisane",
          description: schemaEnabled
            ? "Schema.org WŁĄCZONA — następna publikacja wstrzyknie @graph (WebPage → BlogPosting → Person)."
            : "Schema.org WYŁĄCZONA — publish bez JSON-LD.",
        });
        onOpenChange(false);
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCode2 className="h-5 w-5 text-indigo-500" />
            Schema.org — ustawienia per website
          </DialogTitle>
          <DialogDescription>
            <strong>{website.name}</strong> — master toggle. Generuje JSON-LD na publishu.
            Nie wpływa na Etap 1/2 (Struktura, Treść).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Master toggle */}
          <div className="flex items-center justify-between rounded-md border p-3 bg-muted/30">
            <div>
              <Label className="text-sm font-semibold">Włącz schema.org</Label>
              <p className="text-[11px] text-muted-foreground mt-0.5">
                Gdy OFF — publish działa identycznie jak dziś, bez schema injection.
              </p>
            </div>
            <Switch checked={schemaEnabled} onCheckedChange={setSchemaEnabled} />
          </div>

          {/* PBN-friendly info */}
          <Alert>
            <ShieldCheck className="h-4 w-4" />
            <AlertDescription className="text-xs space-y-2">
              <p>
                <strong>PBN-friendly @graph</strong> (refactor 2026-05-17, commit{" "}
                <code className="text-[10px]">10c0e77</code>):
              </p>
              <ul className="ml-3 list-disc space-y-0.5">
                <li>JEDEN <code>&lt;script type="application/ld+json"&gt;</code> z <code>@graph</code></li>
                <li>Struktura zagnieżdżona: <code>WebPage → mainEntity: BlogPosting → author: Person</code></li>
                <li>ZERO <code>Organization</code>, <code>WebSite</code>, <code>publisher</code>, <code>worksFor</code> (footprint cross-domena)</li>
                <li><code>BreadcrumbList</code>: Home → Kategoria (z seedu) → Artykuł</li>
                <li><code>FAQPage</code> NIE generowany automatycznie</li>
              </ul>
            </AlertDescription>
          </Alert>

          {/* Profile gate info */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <strong>Person (autor):</strong> bierze dane z aktywnego writing_profile mapy
              (gdy <code>profile.schema_enabled=true</code>) — public_author_label, jobTitle, bio,
              photo, knowsAbout, sameAs. Brak profilu lub toggle OFF → fallback minimal Person
              z <code>website.name</code> jako <code>name</code>.
            </AlertDescription>
          </Alert>

          {!schemaEnabled && (
            <Alert variant="default" className="border-amber-500/30">
              <Info className="h-4 w-4" />
              <AlertDescription className="text-xs">
                Schema WYŁĄCZONA. Włącz master toggle powyżej + zapisz, żeby kolejne publish dodawały JSON-LD.
              </AlertDescription>
            </Alert>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Anuluj
          </Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Zapisz
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
